// src/AuthContext.js
import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
} from 'firebase/auth';
import { doc, getDoc, setDoc, collection, getDocs } from 'firebase/firestore';
import { auth, db } from './firebase/config';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState('');

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      if (u) {
        // Load user profile from Firestore
        const profileRef = doc(db, 'users', u.uid);
        const snap = await getDoc(profileRef);
        if (snap.exists()) {
          setUserProfile({ uid: u.uid, email: u.email, ...snap.data() });
        } else {
          // First login — create profile
          const profile = {
            email: u.email,
            displayName: u.email.split('@')[0],
            role: 'user',
            createdAt: Date.now(),
          };
          await setDoc(profileRef, profile);
          setUserProfile({ uid: u.uid, ...profile });
        }
        setUser(u);
      } else {
        setUser(null);
        setUserProfile(null);
      }
      setLoading(false);
    });
    return unsub;
  }, []);

  async function login(email, password) {
    setAuthError('');
    try {
      // Sign in first (required to read Firestore with auth rules)
      const cred = await signInWithEmailAndPassword(auth, email, password);
      // Now authenticated - verify this email is on the allowed list
      const allowedRef = doc(db, 'allowedUsers', email.toLowerCase());
      const allowedSnap = await getDoc(allowedRef);
      if (!allowedSnap.exists()) {
        // Not authorized - sign them back out immediately
        await signOut(auth);
        setAuthError('This email is not authorized. Contact your administrator.');
        return false;
      }
      return true;
    } catch (e) {
      if (e.code === 'auth/user-not-found' || e.code === 'auth/wrong-password' || e.code === 'auth/invalid-credential') {
        setAuthError('Invalid email or password.');
      } else if (e.code === 'auth/too-many-requests') {
        setAuthError('Too many attempts. Try again later.');
      } else {
        setAuthError('Login failed: ' + e.message);
      }
      return false;
    }
  }

  async function logout() {
    await signOut(auth);
  }

  async function getAllUsers() {
    const snap = await getDocs(collection(db, 'users'));
    return snap.docs.map(d => ({ uid: d.id, ...d.data() }));
  }

  return (
    <AuthContext.Provider value={{ user, userProfile, loading, authError, login, logout, getAllUsers }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
